import React from 'react';
import Pagination from '@mui/material/Pagination';
import { StatusBadge } from '../../ui';
import { getPropImagePath, handleImageError } from '../../../utils/imageUtils';

const SurpriseView = ({
  paginatedProps,
  sortedProps,
  totalPages,
  currentPage,
  onPageChange,
  addToCart,
  setSelectedProp,
  setCurrentView
}) => {
  // Random props for inspiration
  const getRandomProps = () => {
    if (paginatedProps.length === 0) return [];
    const shuffled = [...paginatedProps].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, Math.min(12, paginatedProps.length));
  };
  
  const randomProps = getRandomProps();

  return (
    <div className="space-y-6">
      {/* Surprise Header */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white text-center">
        <div className="text-4xl mb-3">🎲✨</div>
        <h2 className="text-2xl font-bold mb-2">Surprise Me!</h2>
        <p className="text-purple-100 mb-4">Discover unexpected props that might spark creativity for your production</p>
        <button
          onClick={() => {
            // Force re-render by updating state
            const currentTime = Date.now();
            setCurrentView('grid');
            setTimeout(() => setCurrentView('surprise'), 1);
          }}
          className="bg-white text-purple-600 px-6 py-2 rounded-lg font-medium hover:bg-purple-50 transition-colors"
        >
          🎲 Show Me New Props
        </button>
      </div>
      
      {randomProps.length > 0 ? (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Random Selection from {sortedProps.length} Available Props</h3>
            <div className="text-sm text-gray-500">
              Showing {randomProps.length} random props (page {currentPage} of {totalPages})
            </div>
          </div>
          
          {/* Random Props Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {randomProps.map(prop => (
              <div key={prop.id} className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
                {/* Image with surprise styling */}
                <div className="aspect-square relative overflow-hidden">
                  <img 
                    src={getPropImagePath(prop)}
                    alt={prop.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => handleImageError(e, prop.category)}
                  />
                  
                  {/* Floating elements */}
                  <div className="absolute top-3 left-3">
                    <StatusBadge status={prop.status} className="shadow-lg" />
                  </div>
                  
                  <div className="absolute top-3 right-3">
                    <span className="bg-white bg-opacity-90 text-gray-800 px-2 py-1 rounded-full text-xs font-medium shadow-lg">
                      {Math.round(prop.distance)} mi
                    </span>
                  </div>
                  
                  {/* Surprise actions overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                      {prop.status === 'available' && (
                        <button
                          onClick={() => addToCart(prop.id)}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg transition-colors"
                        >
                          ⚡ Add to Cart
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedProp(prop)}
                        className="bg-white hover:bg-gray-100 text-gray-900 px-4 py-2 rounded-lg text-sm font-medium shadow-lg transition-colors"
                      >
                        👀 Details
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Enhanced prop info */}
                <div className="p-4">
                  <h3 className="font-bold text-lg mb-2 group-hover:text-purple-600 transition-colors">{prop.name}</h3>
                  
                  <div className="space-y-2 mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">{prop.category}</span>
                      <span className="text-sm text-purple-600 font-medium">{prop.owner}</span>
                    </div>
                    
                    {prop.historical_period && (
                      <div className="text-sm text-gray-500">
                        📅 {prop.historical_period}
                      </div>
                    )}
                  </div>
                  
                  {/* Tags with fun colors */}
                  {prop.tags && prop.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {prop.tags.slice(0, 3).map((tag, index) => {
                        const colors = [
                          'bg-pink-100 text-pink-700',
                          'bg-blue-100 text-blue-700', 
                          'bg-green-100 text-green-700',
                          'bg-yellow-100 text-yellow-700',
                          'bg-purple-100 text-purple-700'
                        ];
                        return (
                          <span 
                            key={tag} 
                            className={`px-2 py-1 rounded-full text-xs font-medium ${colors[index % colors.length]}`}
                          >
                            {tag}
                          </span>
                        );
                      })}
                      {prop.tags.length > 3 && (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium">
                          +{prop.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Fun discovery prompt */}
                  <div className="text-xs text-gray-500 italic border-l-2 border-purple-300 pl-2">
                    💡 "What story could this tell in your production?"
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Inspiration footer */}
          <div className="mt-8 text-center p-6 bg-gray-50 rounded-lg">
            <div className="text-lg font-medium text-gray-700 mb-2">Feeling inspired? 🌟</div>
            <div className="text-sm text-gray-600 mb-4">
              These random props might spark new ideas for scenes, character development, or set design.
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setCurrentView('grid')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
              >
                🔍 Search for Similar Props
              </button>
              <button
                onClick={() => {
                  // Another random set
                  const currentTime = Date.now();
                  setCurrentView('grid');
                  setTimeout(() => setCurrentView('surprise'), 1);
                }}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 transition-colors"
              >
                🎲 Surprise Me Again
              </button>
            </div>
          </div>
          
          {/* Pagination for Surprise View */}
          <Pagination 
            totalPages={totalPages} 
            currentPage={currentPage} 
            onPageChange={onPageChange} 
          />
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">🎲</div>
          <p className="text-lg font-medium mb-2">No Props to Surprise You With!</p>
          <p className="text-sm">Try adjusting your filters to see more props for random discovery.</p>
        </div>
      )}
    </div>
  );
};

export default SurpriseView;